import type { TripAssignment } from "./assignment";
import type { TailLock } from "./boardOps";
import { lockHolder } from "./boardOps";
import { crewIdFromName } from "./duty";
import { blockTimeMin } from "./flightlog";
import { hydrateCrewLedgerEntry, type CrewLedgerEntry, type OpsMoneyCode } from "./opsLedger";
import type { CrewMember, FlightLogEntry, Mission, OpsDeskSettings } from "./types";

/** Stable id so a second post replaces the row instead of adding another. */
export function crewPayEntryId(missionId: string, crewId: string): string {
  return `crewpay:${missionId}:${crewId}`.slice(0, 64);
}

/** Block hours from the flight log (on-blocks − off-blocks), two decimals. */
export function blockHoursForPay(log: FlightLogEntry): number {
  const min = blockTimeMin(log);
  if (min == null || min <= 0) return 0;
  return Math.round((min / 60) * 100) / 100;
}

export type CrewPayDesk = {
  crewLedger?: readonly CrewLedgerEntry[] | null;
  assignments?: readonly TripAssignment[] | null;
  crew?: readonly CrewMember[] | null;
  opsDesk?: OpsDeskSettings | null;
  currency?: OpsMoneyCode;
  tailLocks?: readonly TailLock[] | null;
  vaId?: string;
};

/**
 * One pay line per crew member on the assignment.
 * Amount is block hours × the member's pay override, or the company `payPerHour`.
 * The same `crewpay:<missionId>:<crewId>` key replaces the previous line.
 */
export function postCrewPay(
  desk: CrewPayDesk,
  mission: Mission,
  log: FlightLogEntry,
  now = new Date(),
): CrewLedgerEntry[] {
  const hours = blockHoursForPay(log);
  let ledger = [...(desk.crewLedger ?? [])];
  if (!(hours > 0)) return ledger;
  const companyRate = desk.opsDesk?.crewPay?.payPerHour ?? 0;
  const ids = new Set<string>();
  for (const row of desk.assignments ?? []) {
    if (row.superseded || row.missionId !== mission.id) continue;
    if (row.crewId) ids.add(row.crewId);
    if (row.sicCrewId) ids.add(row.sicCrewId);
  }
  if (!ids.size && log.assignedPilot.trim()) ids.add(crewIdFromName(log.assignedPilot));
  const roster = new Map((desk.crew ?? []).map((member) => [member.id, member]));
  for (const crewId of ids) {
    const override = roster.get(crewId)?.payPerHour;
    const rate = override != null && override > 0 ? override : companyRate;
    if (!(rate > 0)) continue;
    const id = crewPayEntryId(mission.id, crewId);
    const prior = ledger.find((row) => row.id === id);
    const entry = hydrateCrewLedgerEntry({
      id,
      crewId,
      category: "pay",
      amount: Math.round(hours * rate * 100) / 100,
      currency: desk.currency ?? "CAD",
      createdAtUtc: prior?.createdAtUtc ?? now.toISOString(),
      missionId: mission.id,
      flightNumber: (mission.flightNumber || mission.callsign || "").slice(0, 16),
      memo: `Block ${hours.toFixed(2)} h × ${rate}`,
    });
    if (!entry) continue;
    ledger = [entry, ...ledger.filter((row) => row.id !== id)];
  }
  return ledger;
}

/**
 * Hourly pay for the player who hard-locked the tail, posted when the leg closes.
 * The assigned crew line already covers that person. A second line is not added.
 */
export function postSharedTailSalary(
  desk: CrewPayDesk,
  mission: Mission,
  log: FlightLogEntry,
  now = new Date(),
): CrewLedgerEntry[] {
  const ledger = [...(desk.crewLedger ?? [])];
  const holder = lockHolder(desk.tailLocks ?? undefined, mission.aircraftId, desk.vaId);
  if (!holder) return ledger;
  const hours = blockHoursForPay(log);
  if (!(hours > 0)) return ledger;
  const roster = desk.crew ?? [];
  const member =
    roster.find((row) => row.id === holder || row.displayName === holder) ??
    roster.find((row) => crewIdFromName(row.displayName) === crewIdFromName(holder));
  const crewId = member?.id ?? crewIdFromName(holder);
  const id = crewPayEntryId(mission.id, crewId);
  if (ledger.some((row) => row.id === id)) return ledger;
  const companyRate = desk.opsDesk?.crewPay?.payPerHour ?? 0;
  const override = member?.payPerHour;
  const rate = override != null && override > 0 ? override : companyRate;
  if (!(rate > 0)) return ledger;
  const prior = ledger.find((row) => row.id === id);
  const entry = hydrateCrewLedgerEntry({
    id,
    crewId,
    category: "pay",
    amount: Math.round(hours * rate * 100) / 100,
    currency: desk.currency ?? "CAD",
    createdAtUtc: prior?.createdAtUtc ?? now.toISOString(),
    missionId: mission.id,
    flightNumber: (mission.flightNumber || mission.callsign || "").slice(0, 16),
    memo: `Shared tail ${hours.toFixed(2)} h × ${rate}`,
  });
  if (!entry) return ledger;
  return [entry, ...ledger.filter((row) => row.id !== id)];
}

/** Assigned-crew pay, then the shared-tail lock holder when that is someone else. */
export function postLegPay(desk: CrewPayDesk, mission: Mission, log: FlightLogEntry, now = new Date()): CrewLedgerEntry[] {
  const paid = postCrewPay(desk, mission, log, now);
  return postSharedTailSalary({ ...desk, crewLedger: paid }, mission, log, now);
}
